import React, { useEffect, useRef, useState } from "react";
import ImageUploadView, { type MediaPick } from "./ImageUploadView";

/**
 * What `ImageUpload` hands the caller: a picked file as a data URL
 * (`data` + `imgName`), or a media-library pick (`secure_url`, plus its
 * Cloudinary `context` when `withContext` is set).
 */
export type PickedImage = {
  data?: string;
  imgName?: string;
  secure_url?: string;
  context?: unknown;
};

export type ImageUploadContainerProps = {
  // Method syntax on purpose: callers pass handlers typed for the one shape
  // they use (a file, or a media pick), which a method parameter accepts.
  ImageUpload?(img: PickedImage): void;
  onRemove?: () => void;
  image?: string | null;
  clearImage?: boolean;
  uniqueId?: string;
  pendingImg?: string | null;
  /**
   * SCRUM-1595: a media-library pick also hands over the resource's
   * Cloudinary `context` (where its alt lives). Opt-in: several callers store
   * or post the picked object whole, and must keep receiving `{ secure_url }`
   * only.
   */
  withContext?: boolean;
};

const ImageUploadContainer = ({
  ImageUpload,
  onRemove = undefined,
  image = undefined,
  clearImage = undefined,
  uniqueId = undefined,
  pendingImg = undefined,
  withContext = false,
}: ImageUploadContainerProps) => {
  const [imgObject, setImgObject] = useState({ data: "", imgName: "" });
  const [imgSrc, setImgSrc] = useState<string | null>(null);

  const prevClearRef = useRef(clearImage);
  useEffect(() => {
    prevClearRef.current = clearImage;
  });

  useEffect(() => {
    if (image && !imgObject.data) {
      setImgSrc(image);
    }
  }, [image, imgObject.data]);

  useEffect(() => {
    if (clearImage && prevClearRef.current !== clearImage) {
      setImgObject({ data: "", imgName: "" });
      setImgSrc(null);
    }
  }, [clearImage]);

  const remove = () => {
    setImgObject({ data: "", imgName: "" });
    setImgSrc(null);
    onRemove?.();
  };

  const onFileChange = (file?: File) => {
    if (!file) return;
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      // readAsDataURL always yields a string result.
      const dataUrl = reader.result as string;
      setImgSrc(dataUrl);
      const next = { data: dataUrl, imgName: file.name };
      setImgObject(next);
      ImageUpload?.(next);
    };
  };

  const onMediaSelect = (img: MediaPick) => {
    if (!img?.secure_url) return;
    setImgSrc(img.secure_url);
    setImgObject({ data: "", imgName: "" });
    ImageUpload?.(
      withContext && img.context
        ? { secure_url: img.secure_url, context: img.context }
        : { secure_url: img.secure_url }
    );
  };

  return (
    <ImageUploadView
      imgSrc={imgSrc}
      imgObject={imgObject}
      labelText={
        imgObject.data === "" && imgSrc === null ? "Upload Picture" : "Change"
      }
      uniqueId={uniqueId}
      onRemove={remove}
      onFileChange={onFileChange}
      onMediaSelect={onMediaSelect}
      pendingImg={pendingImg}
    />
  );
};

export default ImageUploadContainer;
